import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
    FaBuilding, 
    FaPlus, 
    FaEdit, 
    FaTrash, 
    FaSearch, 
    FaEllipsisV,
    FaInfoCircle,
    FaExclamationTriangle,
    FaFilter,
    FaSortAmountDown,
    FaSortAmountUp,
    FaTimes,
    FaSave,
    FaSpinner,
    FaUserTie
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './Departments.css';
import packageJson from '../../../../package.json';

const Departments = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [company, setCompany] = useState('');
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    
    // Edit modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editDepartment, setEditDepartment] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        fetchDepartments();
        fetchEmployees();
        
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (activeDropdown && !event.target.closest('.department-actions')) {
                setActiveDropdown(null);
            }
            
            if (showEditModal && modalRef.current && !modalRef.current.contains(event.target)) {
                if (!event.target.closest('.swal2-container')) { // Don't close if clicking on SweetAlert
                    setShowEditModal(false);
                }
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown, showEditModal]);

    const fetchDepartments = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            try {
                // Make the actual API call
                const response = await axios.get(`${baseUrl}/api/departments/`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                console.log(response.data[0].company_name);
                
                setDepartments(response.data || []); // Ensure we set an empty array if data is undefined
                setCompany(response.data[0].company_name || '')
            } catch (apiError) {
                console.error('API Error:', apiError);
                
                // If API fails, use mock data for development
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error fetching departments:', error);
            setError('Failed to load departments');
            setDepartments([]); // Set to empty array on error
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            setLoadingEmployees(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseUrl}/api/employees/`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            // Filter managerial employees but exclude those with role 'md'
            const managerialEmployees = response.data.filter(emp => 
                emp.category && 
                emp.category.toLowerCase() === 'managerial' &&
                emp.role !== 'md'  // Exclude employees with role 'md'
            );
            setEmployees(managerialEmployees);
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployees([]);
        } finally {
            setLoadingEmployees(false);
        }
    };

    // Handle edit department
    const handleEdit = (id) => {
        setActiveDropdown(null);
        const departmentToEdit = departments.find(dept => dept.id === id);
        
        if (departmentToEdit) {
            setEditDepartment(departmentToEdit);
            setEditFormData({
                name: departmentToEdit.name,
                description: departmentToEdit.description
            });
            setEditFormErrors({});
            setShowEditModal(true);
        }
    };

    // Handle form input changes
    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    // Validate edit form
    const validateEditForm = () => {
        const errors = {};
        
        if (!editFormData.name.trim()) {
            errors.name = 'Department name is required';
        }
        
        if (!editFormData.description.trim()) {
            errors.description = 'Department description is required';
        } else if (editFormData.description.trim().length < 10) {
            errors.description = 'Description must be at least 10 characters';
        }
        
        setEditFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateEditForm()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            const token = localStorage.getItem('token');
            
            // Prepare data for API
            const updateData = {
                ...editFormData,
                company: editDepartment.company || companyInfo?.id
            };
            
            // Make API call to update department
            await axios.put(`${baseUrl}/api/departments/${editDepartment.id}/`, updateData, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            // Update local state
            setDepartments(departments.map(dept => 
                dept.id === editDepartment.id 
                    ? { ...dept, ...editFormData } 
                    : dept
            ));
            
            // Close modal and show success message
            setShowEditModal(false);
            Swal.fire({
                icon: 'success',
                title: 'Department Updated',
                text: `${editFormData.name} has been updated successfully.`,
                confirmButtonColor: '#1e3a8a'
            });
            
        } catch (error) {
            console.error('Error updating department:', error);
            
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: error.response?.data?.message || 'Failed to update department. Please try again.',
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const toggleDropdown = (id) => {
        setActiveDropdown(activeDropdown === id ? null : id);
    };

    const handleDelete = (id, name) => {
        setActiveDropdown(null);
        
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete the department "${name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    
                    // Make API call to delete department
                    await axios.delete(`${baseUrl}/api/departments/${id}/`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    
                    // Remove the department from the state
                    setDepartments(departments.filter(dept => dept.id !== id));
                    
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: `Department "${name}" has been deleted.`,
                        confirmButtonColor: '#1e3a8a'
                    });
                } catch (error) {
                    console.error('Error deleting department:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete department',
                        confirmButtonColor: '#1e3a8a'
                    });
                }
            }
        });
    };

    // Filter departments based on search term
    const filteredDepartments = departments.filter(department => 
        department.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort departments
    const sortedDepartments = [...filteredDepartments].sort((a, b) => {
        if (sortField === 'name') {
            return sortDirection === 'asc' 
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name);
        } else if (sortField === 'created_at') {
            return sortDirection === 'asc'
                ? new Date(a.created_at) - new Date(b.created_at)
                : new Date(b.created_at) - new Date(a.created_at);
        }
        return 0;
    });

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getDepartmentManagers = (departmentName) => {
        return employees.filter(emp => 
            emp.department_name && 
            emp.department_name.toLowerCase() === departmentName.toLowerCase() &&
            emp.role !== 'md'  // Double check to ensure no 'md' role employees are included
        );
    };

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="departments-container">
                    <div className="departments-header">
                        <div className="title-section">
                            <div className="title-icon">
                                <FaBuilding />
                            </div>
                            <div>
                                <h1>Departments</h1>
                                <p className="subtitle">Manage your company's departments</p>
                            </div>
                        </div>
                        
                        <Link to="/admin-manager/add-department" className="add-department-btn">
                            <FaPlus /> Add Department
                        </Link>
                    </div>
                    
                    <div className="search-and-sort-container">
                        <div className="search-box">
                            <input 
                                type="text"
                                placeholder="Search departments..."
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                        </div>
                        
                        <div className="sort-options">
                            <span className="sort-label">
                                <FaSortAmountDown className="sort-icon" /> Sort by:
                            </span>
                            <div className="sort-buttons">
                                <button 
                                    className={`sort-button ${sortField === 'name' ? 'active' : ''}`}
                                    onClick={() => handleSort('name')}
                                >
                                    Name {sortField === 'name' && (
                                        sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                    )}
                                </button>
                                
                                <button 
                                    className={`sort-button ${sortField === 'created_at' ? 'active' : ''}`}
                                    onClick={() => handleSort('created_at')}
                                >
                                    Date {sortField === 'created_at' && (
                                        sortDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {loading || loadingEmployees ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading departments and employees...</p>
                        </div>
                    ) : error ? (
                        <div className="error-container">
                            <FaExclamationTriangle className="error-icon" />
                            <p>{error}</p>
                            <button 
                                className="retry-button"
                                onClick={fetchDepartments}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : sortedDepartments.length === 0 ? (
                        <div className="empty-state">
                            <FaBuilding className="empty-icon" />
                            <h3>No departments found</h3>
                            <p>
                                {searchTerm 
                                    ? `No departments match "${searchTerm}"`
                                    : "You haven't created any departments yet"}
                            </p>
                            {!searchTerm && (
                                <Link to="/admin-manager/add-department" className="add-first-btn">
                                    <FaPlus /> Create Your First Department
                                </Link>
                            )}
                        </div>
                    ) : (
                        <div className="departments-grid">
                            {sortedDepartments.map(department => (
                                <div key={department.id} className="department-card">
                                    <div className="department-header">
                                        <h3 className="department-name">{department.name}</h3>
                                        <div className="department-actions">
                                            <button 
                                                className="action-toggle"
                                                onClick={() => toggleDropdown(department.id)}
                                            >
                                                <FaEllipsisV />
                                            </button>
                                            {activeDropdown === department.id && (
                                                <div className="action-dropdown">
                                                    <button 
                                                        className="dropdown-item edit"
                                                        onClick={() => handleEdit(department.id)}
                                                    >
                                                        <FaEdit /> Edit
                                                    </button>
                                                    <button 
                                                        className="dropdown-item delete"
                                                        onClick={() => handleDelete(department.id, department.name)}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="department-content">
                                        <p className="department-description">
                                            {department.description}
                                        </p>
                                        <div className="department-managers">
                                            <h4 className="managers-title">
                                                <FaUserTie className="manager-icon" /> 
                                                Department Managers
                                                <span className="manager-count">
                                                    ({getDepartmentManagers(department.name).length})
                                                </span>
                                            </h4>
                                            <div className="managers-list">
                                                {getDepartmentManagers(department.name).length > 0 ? (
                                                    getDepartmentManagers(department.name).map(manager => (
                                                        <div key={manager.id} className="manager-item">
                                                            <div className="manager-info">
                                                                <span className="manager-name">
                                                                    {manager.first_name} {manager.last_name}
                                                                </span>
                                                                <span className="manager-position">
                                                                    {manager.position || 'Manager'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="no-managers">No managers assigned</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="department-footer">
                                        <div className="department-stat">
                                            <span className="stat-label">Created:</span>
                                            <span style={{fontSize: '15px'}} className="stat-value">{formatDate(department.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Edit Department Modal */}
                    {showEditModal && editDepartment && (
                        <div className="modal-overlay">
                            <div className="edit-department-modal" ref={modalRef}>
                                <div className="modal-header">
                                    <h2>
                                        <FaEdit className="modal-icon" />
                                        Edit Department
                                    </h2>
                                    <button 
                                        className="close-modal-btn"
                                        onClick={() => setShowEditModal(false)}
                                        disabled={isSubmitting}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>
                                
                                <div className="modal-body">
                                    <form onSubmit={handleEditSubmit}>
                                        <div className="form-group">
                                            <label htmlFor="name">
                                                Department Name <span className="required">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={editFormData.name}
                                                onChange={handleEditFormChange}
                                                placeholder="Enter department name"
                                                className={editFormErrors.name ? 'error' : ''}
                                                disabled={isSubmitting}
                                            />
                                            {editFormErrors.name && (
                                                <div className="error-message">
                                                    <FaInfoCircle /> {editFormErrors.name}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="form-group">
                                            <label htmlFor="description">
                                                Department Description <span className="required">*</span>
                                            </label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={editFormData.description}
                                                onChange={handleEditFormChange}
                                                placeholder="Enter department description"
                                                rows="4"
                                                className={editFormErrors.description ? 'error' : ''}
                                                disabled={isSubmitting}
                                            ></textarea>
                                            {editFormErrors.description && (
                                                <div className="error-message">
                                                    <FaInfoCircle /> {editFormErrors.description}
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="modal-footer">
                                            <button
                                                type="button"
                                                className="cancel-button"
                                                onClick={() => setShowEditModal(false)}
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="save-button"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <FaSpinner className="spinner-icon" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSave /> Save Changes
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Inline styles for the modal since the CSS file changes were rejected */}
            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    animation: fadeIn 0.3s ease;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                .edit-department-modal {
                    background-color: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 600px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    animation: slideIn 0.3s ease;
                    position: relative;
                }
                
                @keyframes slideIn {
                    from { transform: translateY(-20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                    position: sticky;
                    top: 0;
                    background-color: white;
                    border-radius: 12px 12px 0 0;
                    z-index: 1;
                }
                
                .modal-header h2 {
                    display: flex;
                    align-items: center;
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #1e3a8a;
                    margin: 0;
                }
                
                .modal-icon {
                    margin-right: 0.75rem;
                    color: #1e3a8a;
                }
                
                .close-modal-btn {
                    background: none;
                    border: none;
                    color: #64748b;
                    font-size: 1.25rem;
                    cursor: pointer;
                    transition: color 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                }
                
                .close-modal-btn:hover {
                    color: #1f2937;
                    background-color: #f1f5f9;
                }
                
                .modal-body {
                    padding: 1.5rem;
                }
                
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #e5e7eb;
                }
                
                .cancel-button,
                .save-button {
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.2s;
                }
                
                .cancel-button {
                    background-color: #f1f5f9;
                    color: #64748b;
                    border: none;
                }
                
                .cancel-button:hover:not(:disabled) {
                    background-color: #e2e8f0;
                }
                
                .save-button {
                    background-color: #1e3a8a;
                    color: white;
                    border: none;
                }
                
                .save-button:hover:not(:disabled) {
                    background-color: #1e40af;
                    transform: translateY(-2px);
                }
                
                .cancel-button:disabled,
                .save-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .spinner-icon {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 640px) {
                    .edit-department-modal {
                        width: 95%;
                    }
                    
                    .modal-header {
                        padding: 1rem;
                    }
                    
                    .modal-body {
                        padding: 1rem;
                    }
                    
                    .modal-footer {
                        flex-direction: column;
                    }
                    
                    .cancel-button,
                    .save-button {
                        width: 100%;
                        justify-content: center;
                    }
                }
                
                .department-managers {
                    margin-top: 1rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e5e7eb;
                }
                
                .managers-title {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    color: #4b5563;
                    margin-bottom: 0.75rem;
                }
                
                .manager-icon {
                    color: #1e3a8a;
                    font-size: 0.875rem;
                }
                
                .manager-count {
                    font-size: 0.75rem;
                    color: #6b7280;
                    margin-left: 0.5rem;
                }
                
                .managers-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    max-height: 150px;
                    overflow-y: auto;
                }
                
                .manager-item {
                    display: flex;
                    align-items: center;
                    padding: 0.5rem;
                    background-color: #f8fafc;
                    border-radius: 6px;
                    transition: background-color 0.2s;
                }
                
                .manager-item:hover {
                    background-color: #f1f5f9;
                }
                
                .manager-info {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }
                
                .manager-name {
                    font-size: 0.875rem;
                    color: #1f2937;
                    font-weight: 500;
                }
                
                .manager-position {
                    font-size: 0.75rem;
                    color: #6b7280;
                }
                
                .no-managers {
                    font-size: 0.875rem;
                    color: #6b7280;
                    font-style: italic;
                    text-align: center;
                    padding: 0.5rem;
                }

                .search-and-sort-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    margin-bottom: 20px;
                    padding: 0 20px;
                }

                .search-box {
                    display: flex;
                    align-items: center;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px 12px;
                    flex: 1;
                    max-width: 400px;
                }

                .search-box input {
                    border: none;
                    outline: none;
                    padding: 4px 8px;
                    width: 100%;
                    font-size: 14px;
                }

                .search-icon {
                    font-size: 16px;
                }

                .sort-options {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .sort-label {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #64748b;
                    font-size: 14px;
                }

                .sort-icon {
                    font-size: 14px;
                }

                .sort-buttons {
                    display: flex;
                    gap: 8px;
                }

                .sort-button {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    background: white;
                    color: #64748b;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .sort-button:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .sort-button.active {
                    background: #1e3a8a;
                    color: white;
                    border-color: #1e3a8a;
                }

                .sort-button.active:hover {
                    background: #1e40af;
                }

                @media (max-width: 768px) {
                    .search-and-sort-container {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                    }

                    .search-box {
                        max-width: none;
                    }

                    .sort-options {
                        justify-content: space-between;
                    }
                }
            `}</style>
        </div>
    );
};

export default Departments;